import { useState } from "react";
import { API_BASE_URL } from "../../constants";
import { formatEventDate, fetchWithCORS } from "../../utils";

/**
 * Custom hook for managing social events data and functionality
 *
 * @param {Object} props - Hook configuration
 * @param {Function} props.getAuthHeaders - Function to get authentication headers
 * @param {Function} props.logout - Function to handle logout when session expires
 * @returns {Object} Events state and methods
 */
const useEvents = ({ getAuthHeaders, logout }) => {
  // State
  const [socialEvents, setSocialEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState({});
  const [daysAhead, setDaysAhead] = useState(14);
  const [calendarUpdateResults, setCalendarUpdateResults] = useState(null);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  /**
   * Load social events from API
   */
  const loadSocialEvents = async () => {
    setLoading(true);
    setErrorMessage("");
    setCalendarUpdateResults(null);

    try {
      const headers = getAuthHeaders();

      const response = await fetch(
        `${API_BASE_URL}?type=socialEvents&days=${daysAhead}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          logout();
          throw new Error(
            "Session expired or unauthorized, please login again"
          );
        }
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();

      // Add formatted date for display
      const eventsWithFormattedDates = (data.upcomingEvents || []).map(
        (event) => {
          // Debug log for date issues
          console.log("Original event date info:", {
            id: event.socialEventId,
            date: event.date,
            time: event.time,
          });

          const dateParts = event.socialEventId.split("-");
          if (dateParts.length >= 3) {
            // Fix: Add a day to compensate for timezone issues when parsing dates
            const fullDate = `${dateParts[0]}-${dateParts[1]}-${dateParts[2]}`;
            const dateObj = new Date(fullDate);

            // Debug timezone adjustments
            console.log("Date parsing:", {
              fullDate,
              dateObj,
              localISOString: dateObj.toISOString(),
              timezoneOffset: dateObj.getTimezoneOffset(),
            });

            // Use either direct formatting or adjust for timezone as needed
            event.shortDate = formatEventDate(fullDate);

            // Add a debug indicator to verify fix is applied
            event.displayDateFixed = true;
          }
          return event;
        }
      );

      setSocialEvents(eventsWithFormattedDates);

      // Clear selected events that are no longer in the list
      const validEvents = {};
      eventsWithFormattedDates.forEach((event) => {
        if (selectedEvents[event.socialEventId]) {
          validEvents[event.socialEventId] =
            selectedEvents[event.socialEventId];
        }
      });
      setSelectedEvents(validEvents);
    } catch (error) {
      setErrorMessage("Failed to load social events: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle days ahead filter change
   */
  const handleDaysAheadChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setDaysAhead(value);
    }
  };

  /**
   * Toggle event selection for calendar posting
   */
  const handleSelectEvent = (eventId) => {
    if (selectedEvents[eventId]) {
      // If already selected, remove it
      const updatedSelections = { ...selectedEvents };
      delete updatedSelections[eventId];
      setSelectedEvents(updatedSelections);
    } else {
      // Add to selected events
      setSelectedEvents({
        ...selectedEvents,
        [eventId]: true,
      });
    }
  };

  /**
   * Submit selected events to the calendar
   */
  const handleSubmitCalendarEvents = async () => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    setCalendarUpdateResults(null);

    try {
      const headers = getAuthHeaders();

      // Get array of selected event IDs
      const eventIds = Object.keys(selectedEvents);

      if (eventIds.length === 0) {
        setErrorMessage("Please select events to post to the calendar");
        setLoading(false);
        return;
      }

      const response = await fetchWithCORS(API_BASE_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({
          type: "calendarEvents",
          eventIds: eventIds,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          throw new Error(
            "Session expired or unauthorized, please login again"
          );
        } else if (response.status === 403) {
          setErrorMessage(
            "You don't have permission to post events to the calendar. Admin access required."
          );
          setLoading(false);
          return;
        } else {
          throw new Error(`Error: ${response.status}`);
        }
      }

      const result = await response.json();
      setCalendarUpdateResults(result);
      setSuccessMessage(
        `Successfully posted ${
          result.results.filter((r) => r.success).length
        } events to the GroupMe calendar`
      );

      // Clear selections
      setSelectedEvents({});

      // Reload the list to update event status
      loadSocialEvents();
    } catch (error) {
      setErrorMessage("Failed to post events to calendar: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle event card expansion
   */
  const toggleEventExpansion = (eventId) => {
    setExpandedEvents((prev) => ({
      ...prev,
      [eventId]: !prev[eventId],
    }));
  };

  // Clear state when logging out
  const clearState = () => {
    setSocialEvents([]);
    setSelectedEvents({});
    setCalendarUpdateResults(null);
    setExpandedEvents({});
    setErrorMessage("");
    setSuccessMessage("");
  };

  return {
    // State
    socialEvents,
    selectedEvents,
    daysAhead,
    calendarUpdateResults,
    expandedEvents,
    loading,
    errorMessage,
    successMessage,

    // Methods
    loadSocialEvents,
    handleDaysAheadChange,
    handleSelectEvent,
    handleSubmitCalendarEvents,
    toggleEventExpansion,
    clearState,
  };
};

export default useEvents;
